'use client'
import CompanyListingCardSkeleton from '@/app/[lng]/components/Skeletons/CompanyListingCardSkeleton'
import { useGetAdsQuery } from '@/app/globalRedux/api/adsSlice'
import { useGetCompanySearchQuery } from '@/app/globalRedux/api/searchSlice'
import { setCompanyFilter } from '@/app/globalRedux/filter/companyFilter'
import { RootState } from '@/app/globalRedux/store'
import { AdBannerScheduleType } from '@/app/types/adType'
import { Company, CompanyResponse } from '@/app/types/companySearchType'
import LimitPagination from '@/components/LimitPagination'
import { convertNaNToOne } from '@/lib/formatUtils'
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation'
import { useEffect, useOptimistic, useState, useTransition } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import InBetweenAds from '../../(AdComponent)/InBetweenAds'
import SearchListingCompanyCard from '../../../../components/Cards/SearchListingCompanyCard'
import CustomErrorBoundary from '../../../../ErrorBoundary'
import EmptyResult from '../../EmptyResult'
import CompaniesListBlockerCard from '../CompaniesListBlockerCard'
export const adInterval = 3

const CompanyList = ({ search }: { search: string }) => {
  const params = useParams<{ lng: string; companyPageNo: string[] }>()
  const pathName = usePathname()
  const router = useRouter()
  const dispatch = useDispatch()
  const searchParams = useSearchParams()
  const isProductBasedListing = pathName
    ?.split('/')
    ?.includes('product-company-tab')
  const [isPending, startTransition] = useTransition()
  const companyFilter = useSelector((state: RootState) => state.companyFilter)
  const [adsData, setAdsData] = useState<AdBannerScheduleType[]>([])
  const commonFilter = useSelector((state: RootState) => state.commonFilter)
  const productFilter = useSelector((state: RootState) => state.productFilter)
  const [optimisticPageNo, setOptimisticPageNo] = useOptimistic<number>(
    convertNaNToOne(Number(params?.companyPageNo?.[0] || 1))
  )

  const { data: companyData, isFetching: isCompanyDataFetching } =
    useGetCompanySearchQuery<{
      data: CompanyResponse
      isFetching: boolean
    }>(
      {
        search_on: search,
        companyFilter: companyFilter,
        commonFilter: commonFilter,
        productFilter: productFilter,
        lang: String(params?.lng),
        pageNo: convertNaNToOne(Number(params?.companyPageNo?.[0] || 1)),
      },
      {
        skip: companyFilter.isRouteUpdating,
      }
    )
  const { data: companyAds } = useGetAdsQuery<{
    data: AdBannerScheduleType[]
    isLoading: boolean
    isError: boolean
    isFetching: boolean
  }>({
    page: `Search Results ${isProductBasedListing ? 'Products' : 'Companies'}`,
    lang: params.lng,
  })
  const isCompanyListLoading = () => {
    if (!params?.companyPageNo?.[0] && !isCompanyDataFetching) {
      return false
    }
    return isPending || isCompanyDataFetching
  }

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setOptimisticPageNo(page)
      if (isProductBasedListing) {
        router.push(
          `/${params.lng}/search/product-company-tab/${page}?searchTerm=${searchParams.get('searchTerm') ?? ''}`
        )
      } else {
        router.push(
          `/${params.lng}/search/company-tab/${page}?searchTerm=${searchParams.get('searchTerm') ?? ''}`
        )
      }
    })
  }

  useEffect(() => {
    dispatch(
      setCompanyFilter({
        isRouteUpdating: isPending,
      })
    )
  }, [isPending])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [pathName])
  useEffect(() => {
    if (companyAds) {
      const adFilter5 =
        companyAds.filter((ad) => ad.ad_position.name === 'Ad Position 5')[0] ??
        {}
      const adFilter6 =
        companyAds.filter((ad) => ad.ad_position.name === 'Ad Position 6')[0] ??
        {}
      const adFilter7 =
        companyAds.filter((ad) => ad.ad_position.name === 'Ad Position 7')[0] ??
        {}

      const adsData: AdBannerScheduleType[] = [adFilter5, adFilter6, adFilter7]

      setAdsData(adsData)
    }
  }, [companyAds])
  return (
    <div className="pt-[12.5px] border-t border-gray_btn border-solid mobile-width">
      {isCompanyListLoading() ? (
        <CompanyListingCardSkeleton />
      ) : companyData?.status == 'warning' ? (
        <CompaniesListBlockerCard
          subscription={companyData?.current_subscription ?? ''}
        />
      ) : companyData?.company?.length > 0 ? (
        <div className="gap-4 flex flex-col">
          {companyData?.company?.map((company: Company, index: number) => (
            <div key={company.id}>
              <CustomErrorBoundary key={company.id + 'error_boundary'}>
                <SearchListingCompanyCard company={company} showLogo={true} />
              </CustomErrorBoundary>
              <span className="company-list-ad">
                {(index + 1) % adInterval === 0 && (
                  <InBetweenAds
                    index={index}
                    adsData={
                      adsData[Math.floor(index / adInterval) % adsData.length]
                    }
                  />
                )}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <EmptyResult searchTerm={companyFilter?.searchTerm ?? ''} />
      )}
      {companyData?.count > 0 && (
        <div className="mt-[35px] border-t-2 border-t-[#e6e6e6] border-solid mobile:mt-[10px]">
          <LimitPagination
            currentPage={optimisticPageNo}
            pageCount={Math.ceil(companyData?.count / 10) || 1}
            visiblePages={5}
            onPageChange={(page) => handlePageChange(page)}
          />
        </div>
      )}
    </div>
  )
}

export default CompanyList
